import React, { useState, useRef, useEffect } from "react";
import "../styles/MyInfoPage.css";
import { z } from "zod";
import MyPageSidebar from "./MyPageSidebar";
import { useNavigate } from "react-router-dom";
import {useChangePasswordMutation, useConfirmEmailMutation, useVerifyEmailMutation} from "../../api/FindIdAPI";

const passwordSchema = z.object({
    currentPassword: z.string().min(1, "현재 비밀번호를 입력해주세요."),
    newPassword: z.string()
        .min(8, "비밀번호는 8자 이상이어야 합니다.")
        .max(16, "비밀번호는 16자 이하여야 합니다.")
        .regex(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,16}$/,
            "비밀번호는 영어, 숫자, 특수문자(@,$,!,%,*,#,?,&)를 포함해야 합니다."),
    confirmPassword: z.string()
});

// 🔥 Zod 스키마의 refine 기능을 이용하여 추가 검사
const validatePasswords = passwordSchema.refine((data) => data.newPassword !== data.currentPassword, {
    message: "새 비밀번호는 현재 비밀번호와 다르게 설정해야 합니다.",
    path: ["newPassword"],
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: "새 비밀번호와 확인 비밀번호가 일치하지 않습니다.",
    path: ["confirmPassword"],
});

const MyInfoPage = () => {
    const navigate = useNavigate();
    const [myData, setMyData] = useState(null); // ✅ 사용자 정보 저장
    const [authCode, setAuthCode] = useState("");
    const [isVerified, setIsVerified] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const authInputRef = useRef(null);
    const timerRef = useRef(null);
    // ✅ 비밀번호 변경 모달 상태
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [emailVerified, setEmailVerified] = useState(false);

    const verifyEmailMutation = useVerifyEmailMutation();
    const confirmEmailMutation = useConfirmEmailMutation();
    const changePasswordMuation = useChangePasswordMutation();

    // ✅ 마이페이지 데이터 가져오기
    useEffect(() => {
        const fetchMyPageData = async () => {
            try {
                const response = await fetch("/api/mypage/info"); // ✅ 실제 API 호출
                if (response.ok) {
                    const data = await response.json();
                    setMyData(data.myManagementData || {}); // ✅ 사용자 데이터 설정

                    // ✅ 이메일 인증 여부 확인
                    setEmailVerified(data.myManagementData?.emailVerified || false);
                }
            } catch (error) {
                console.error("마이페이지 데이터 불러오기 실패:", error);
            }
        };

        fetchMyPageData();
    }, []);

    // ✅ 이메일 인증 요청
    const handleVerifyEmail = () => {
        setSendingEmail(true);

        verifyEmailMutation.mutate(
            { name: myData?.name, email: myData?.email },
            {
                onSuccess: () => {
                    alert("인증번호가 발송되었습니다.");
                    startTimer();
                    setTimeout(() => authInputRef.current?.focus(), 100);
                },
                onError: (error) => alert(error.message),
            }
        );
    };

    // ✅ 이메일 인증번호 검증
    const handleConfirmEmail = () => {
        const payload = {
            email: myData?.email,
            authCode,
            timeStamp: new Date().getTime(), // ✅ 현재 시간 추가
        };

        console.log("🔹 요청 데이터:", payload); // 요청 데이터 확인

        confirmEmailMutation.mutate(payload, {
            onSuccess: () => {
                alert("이메일 인증 성공!");
                setIsVerified(true);
            },
            onError: (error) => alert(error.message),
        });
    };

    // 🔥 타이머 시작 함수
    const startTimer = () => {
        setTimeLeft(180);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => (prev <= 1 ? clearInterval(timerRef.current) || 0 : prev - 1));
        }, 1000);
    };

    // ⏳ 타이머 포맷 (MM:SS)
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (!isVerified) {
            return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
        } else {
            return "0:00";
        }
    };

    // 🔥 비밀번호 입력 시 실시간 검증
    const handlePasswordChangeLive = (field, value) => {
        let updatedData = {
            currentPassword,
            newPassword,
            confirmPassword,
            [field]: value
        };

        if (field === "newPassword") setNewPassword(value);
        if (field === "confirmPassword") setConfirmPassword(value);
        if (field === "currentPassword") setCurrentPassword(value);

        const result = validatePasswords.safeParse(updatedData);

        if (!result.success) {
            const formattedErrors = result.error.format();
            setErrors({
                currentPassword: formattedErrors.currentPassword?._errors[0] || "",
                newPassword: formattedErrors.newPassword?._errors[0] || "",
                confirmPassword: formattedErrors.confirmPassword?._errors[0] || ""
            });
        } else {
            setErrors({}); // 모든 조건 만족 시 에러 초기화
        }
    };

    // 🔥 비밀번호 변경 처리
    const handlePasswordChange = () => {
        const result = validatePasswords.safeParse({
            currentPassword,
            newPassword,
            confirmPassword
        });

        if (!result.success) {
            const formattedErrors = result.error.format();
            setErrors({
                currentPassword: formattedErrors.currentPassword?._errors[0] || "",
                newPassword: formattedErrors.newPassword?._errors[0] || "",
                confirmPassword: formattedErrors.confirmPassword?._errors[0] || ""
            });
            return;
        }

        // 비밀번호 변경 API 호출
        const payload = {
            loginId : myData.id,
            newPassword: newPassword,
            newPasswordConfirm: confirmPassword,
        };

        console.log("🔹 요청 데이터:", payload); // 요청 데이터 확인

        changePasswordMuation.mutate(payload, {
            onSuccess: () => {
                alert("비밀번호가 변경되었습니다.");
                closePasswordModal();
            },
            onError: (error) => alert(error.message),
        });
    };

    // 🔥 비밀번호 변경 모달 열기
    const openPasswordModal = () => {
        setIsPasswordModalOpen(true);
    };

    // 🔥 비밀번호 변경 모달 닫기
    const closePasswordModal = () => {
        setIsPasswordModalOpen(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({}); // 오류 초기화
    };

    if (!myData) return <p>로딩 중...</p>; // ✅ 데이터 로딩 처리

    return (
        <div className="myinfo-container">
            <MyPageSidebar />
            <div className="myinfo-content">
                <h2 className="myinfo-title">개인정보 관리</h2>

                <div className="myinfo-section">
                    <label className="myinfo-label">아이디</label>
                    <input type="text" className="myinfo-input" value={myData.id || ""} readOnly />
                </div>

                <div className="myinfo-section">
                    <label className="myinfo-label">비밀번호</label>
                    <button className="myinfo-btn" onClick={openPasswordModal}>비밀번호 변경하기</button>
                </div>

                {/* ✅ 비밀번호 변경 모달 */}
                {isPasswordModalOpen && (
                    <div className="modal-overlay">
                        <div className="password-modal">
                            <h3 className="modal-title">비밀번호 변경</h3>
                            <button className="modal-close" onClick={closePasswordModal}>✖</button>

                            <div className="modal-input-group">
                                <label>현재 비밀번호</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => handlePasswordChangeLive("currentPassword", e.target.value)}
                                />
                                {errors.currentPassword &&
                                    <p className="my-info-error-message">{errors.currentPassword}</p>}
                            </div>

                            <div className="modal-input-group">
                                <label>변경할 비밀번호</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => handlePasswordChangeLive("newPassword", e.target.value)}
                                />
                                {errors.newPassword && <p className="my-info-error-message">{errors.newPassword}</p>}
                            </div>
                            <div className="modal-input-group">
                                <label>변경할 비밀번호 확인</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => handlePasswordChangeLive("confirmPassword", e.target.value)}
                                />
                                {errors.confirmPassword &&
                                    <p className="my-info-error-message">{errors.confirmPassword}</p>}
                            </div>

                            <div className="modal-buttons">
                                <button className="submit-btn" onClick={handlePasswordChange}>변경하기</button>
                                <button className="cancel-btn" onClick={closePasswordModal}>취소</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="myinfo-section">
                    <label className="myinfo-label">이름</label>
                    <input type="text" className="myinfo-input" value={myData.name || ""} readOnly />
                </div>

                <div className="myinfo-section">
                    {emailVerified ? (
                        <><label className="myinfo-success-label">이메일 (등록 완료)</label>
                        <div className="myinfo-email-verification">
                            <input type="text" className="myinfo-input myinfo-email-input" value={myData.email || ""} readOnly />
                            <button className={"myinfo-btn-disabled"} disabled={true}>인증번호발송</button>
                        </div>
                        </>
                    ) : (
                        <><label className="myinfo-label">이메일 (선택 등록)</label>
                        <div className="myinfo-email-verification">
                            <input type="text" className="myinfo-input myinfo-email-input" value={myData.email || ""} readOnly />
                            <button className={isVerified ? "myinfo-btn-disabled" : "myinfo-btn"} disabled={isVerified} onClick={handleVerifyEmail}>인증번호발송</button>
                        </div>
                        </>
                    )}
                </div>

                {sendingEmail && (
                    <div className="myinfo-section">
                        <label className="myinfo-label">인증번호 입력</label>
                        <div className="myinfo-email-auth">
                            <input
                                ref={authInputRef}
                                type="text"
                                className="myinfo-input myinfo-auth-input"
                                placeholder="인증번호를 입력해주세요"
                                value={authCode}
                                disabled={!sendingEmail}
                                onChange={(e) => setAuthCode(e.target.value)}
                            />
                            <span className="myinfo-timer">{timeLeft > 0 ? formatTime(timeLeft) : "0:00"}</span>
                            <button className={isVerified ? "myinfo-btn-disabled" : "myinfo-btn"} onClick={handleConfirmEmail}>확인</button>
                        </div>
                    </div>
                )}

                {isVerified && <p className="success-message">✅ 이메일 인증이 완료되었습니다.</p>}

                <div className="myinfo-section">
                    <label className="myinfo-label">전화번호</label>
                    <input type="text" className="myinfo-input" value={myData.phone || ""} readOnly/>
                </div>

                <div className="myinfo-section">
                    <button className="myinfo-withdraw" onClick={() => {
                        navigate('/mypage/withdrawal')
                    }}>회원탈퇴
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MyInfoPage;
